import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarClock,
  ExternalLink,
  Flame,
  CheckCircle2,
  Clock,
  FileCheck,
  Package,
  Layers,
  Sparkles,
  CreditCard,
  Receipt,
  CheckCircle,
} from "lucide-react";
import { api } from "@/lib/api";

const STAGES = ["Enquiry", "InProgress", "PostProduction", "Completed"];
const STAGE_LABEL = {
  Enquiry: "Enquiry Received",
  InProgress: "In Progress / Shooting",
  PostProduction: "Post-Production & Editing",
  Completed: "Completed & Delivered",
};

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

export default function PublicProjectStatus() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicClientData = async () => {
      try {
        setLoading(true);
        // Fetch public project details and payment history
        const [clientRes, paymentsRes] = await Promise.all([
          api.get(`/public/clients/${id}`),
          api.get(`/public/clients/${id}/payments`).catch(() => ({ data: [] })),
        ]);

        setClient(clientRes.data);
        setPayments(paymentsRes.data || []);
      } catch (err) {
        setError("Project not found or private link has expired.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPublicClientData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-widest">
            Fetching project details…
          </p>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-md w-full text-center space-y-3 shadow-sm">
          <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto">
            <Clock className="w-6 h-6" />
          </div>
          <h2 className="font-serif text-xl text-gray-900">Project Not Found</h2>
          <p className="text-xs text-gray-500">{error || "Please check your portal link."}</p>
        </div>
      </div>
    );
  }

  const currentStageIndex = STAGES.indexOf(client.current_stage);

  // Financial calculations
  const totalCommitted = client.package?.total_committed_amount || 0;
  const totalPaid = payments.reduce((sum, p) => sum + (p.amount_received || 0), 0);
  const remainingBalance = Math.max(0, totalCommitted - totalPaid);
  const paymentPercentage = totalCommitted > 0 ? Math.min(100, Math.round((totalPaid / totalCommitted) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header Branding & Client Banner */}
        <div className="bg-white border border-gray-200/80 rounded-2xl p-6 sm:p-8 shadow-sm relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-6">
            <div>
              <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-blue-600 flex items-center gap-1.5">
                 Project Status For
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl text-gray-900 mt-1.5 tracking-tight flex items-center gap-3">
                {client.name}
                {client.is_fast_track && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                    <Flame className="w-3 h-3 text-amber-600" /> Fast Track
                  </span>
                )}
              </h1>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 sm:px-4 text-left sm:text-right shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                Target Delivery
              </div>
              <div className="text-sm font-semibold tabular-nums text-gray-900 mt-0.5 flex items-center sm:justify-end gap-1.5">
                <CalendarClock className="w-4 h-4 text-gray-400" />
                {formatDate(client.expected_delivery_date)}
              </div>
            </div>
          </div>

          {/* Stepper Progress Bar */}
          <div className="pt-6">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-4">
              Current Stage Progress
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {STAGES.map((stage, idx) => {
                const isActive = stage === client.current_stage;
                const isPassed = currentStageIndex > idx;

                return (
                  <div
                    key={stage}
                    className={`p-3 rounded-xl border transition-all ${
                      isActive
                        ? "bg-blue-50/80 border-blue-500/80 text-blue-900"
                        : isPassed
                        ? "bg-emerald-50/40 border-emerald-200 text-emerald-900"
                        : "bg-gray-50/50 border-gray-100 text-gray-400"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {isPassed ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : isActive ? (
                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-ping" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-300" />
                      )}
                      <span className="text-[10px] uppercase tracking-wider font-bold opacity-75">
                        Stage 0{idx + 1}
                      </span>
                    </div>
                    <div className="text-xs font-semibold leading-tight">
                      {STAGE_LABEL[stage]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Main Column: Events, Deliverable Links & Payment History */}
          <div className="md:col-span-2 space-y-6">
            
            {/* Scheduled Events & Links */}
            <div className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="font-serif text-lg text-gray-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" /> Events & Gallery Links
                </h3>
                <span className="text-xs text-gray-400">
                  {client.events?.length || 0} event(s)
                </span>
              </div>

              <div className="space-y-4">
                {(client.events || []).map((ev, idx) => (
                  <div
                    key={ev.id || idx}
                    className="border border-gray-100 rounded-xl p-4 bg-gray-50/40 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-sm text-gray-900">
                          {ev.event_name}
                        </h4>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                          <CalendarClock className="w-3.5 h-3.5 text-gray-400" />
                          {formatDate(ev.event_date)}
                        </div>
                      </div>
                    </div>

                    {/* Links & Galleries */}
                    {ev.links && ev.links.length > 0 ? (
                      <div className="pt-2 border-t border-gray-100/80 space-y-2">
                        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                          Available Links & Galleries
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          {ev.links.map((link, lIdx) => (
                            <a
                              key={lIdx}
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center justify-between px-3.5 py-2.5 rounded-lg bg-white border border-gray-200/80 hover:border-blue-400 text-xs text-blue-600 hover:text-blue-700 font-medium transition-all shadow-2xs group"
                            >
                              <span className="text-gray-800 font-semibold truncate mr-2">
                                {link.label || "Gallery Link"}
                              </span>
                              <span className="inline-flex items-center text-[11px] text-blue-600 group-hover:translate-x-0.5 transition-transform shrink-0">
                                Open Link <ExternalLink className="w-3 h-3 ml-1" />
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 italic pt-1">
                        Links will appear here as soon as files/previews are published.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment History Ledger */}
            <div className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="font-serif text-lg text-gray-900 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-blue-600" /> Payment History Ledger
                </h3>
                <span className="text-xs text-gray-400">
                  {payments.length} transaction(s)
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <div className="py-4 text-center text-xs text-gray-400 italic">
                    No payment records logged yet.
                  </div>
                ) : (
                  payments.map((p, idx) => (
                    <div key={p.id || idx} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-gray-900 tabular-nums">
                          ₹{(p.amount_received || 0).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {formatDate(p.date_of_payment)} · {p.payment_mode || "Cash"}
                          {p.note ? ` · ${p.note}` : ""}
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle className="w-3 h-3" /> Paid
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>

          {/* Sidebar Column: Package & Financial Summary */}
          <div className="space-y-6">
            
            {/* Payment & Balance Summary Card */}
            <div className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="border-b border-gray-100 pb-3">
                <h3 className="font-serif text-lg text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-600" /> Payment Summary
                </h3>
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-500">Total Committed</span>
                  <span className="font-bold text-gray-900 tabular-nums">
                    ₹{totalCommitted.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-500">Total Paid</span>
                  <span className="font-bold text-emerald-600 tabular-nums">
                    ₹{totalPaid.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-t border-gray-100 pt-2">
                  <span className="font-semibold text-gray-700">Balance Due</span>
                  <span className="font-bold text-blue-600 text-sm tabular-nums">
                    ₹{remainingBalance.toLocaleString()}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="pt-2">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1 font-medium">
                    <span>Payment Progress</span>
                    <span>{paymentPercentage}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${paymentPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Package Details Card */}
            <div className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="border-b border-gray-100 pb-3">
                <h3 className="font-serif text-lg text-gray-900 flex items-center gap-2">
                  <Package className="w-4 h-4 text-blue-600" /> Package Details
                </h3>
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                  <span className="text-gray-500">Album Type</span>
                  <span className="font-semibold text-gray-900">
                    {client.package?.album_type || "Lustre"}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                  <span className="text-gray-500">Album Pages</span>
                  <span className="font-semibold text-gray-900">
                    {client.package?.album_pages || 30} Pages
                  </span>
                </div>

                <div>
                  <div className="text-gray-500 mb-2 font-medium">Deliverables & Addons:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(client.package?.addons || []).length === 0 ? (
                      <span className="text-gray-400 italic">Standard Package</span>
                    ) : (
                      client.package.addons.map((a) => (
                        <span
                          key={a}
                          className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100"
                        >
                          {a}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Support Notice */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-5 text-center space-y-1.5">
              <FileCheck className="w-5 h-5 text-blue-600 mx-auto" />
              <div className="text-xs font-semibold text-blue-900">
                Questions about your project?
              </div>
              <p className="text-[11px] text-blue-700/80 leading-relaxed">
                Contact our studio directly if you need clarification on payments or upcoming deliveries.
              </p>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}